import { useEffect, useState } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { Pencil, Plus, Trash2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { db } from '../../firebase/config';
import { uploadToStorage } from '../../database/storage';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import PageHeader from './shell/PageHeader';

const EMPTY = { name: '', role: '', description: '', link: '' };

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-heat">*</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const StaffManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'staff'), orderBy('order', 'asc')));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      toast.error('Could not load staff.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setPhotoFile(null);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '',
      role: s.role || '',
      description: s.description || '',
      link: s.link || '',
    });
    setPhotoFile(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim()) {
      toast.error('Name and role are required.');
      return;
    }
    const link = form.link.trim();
    if (link && !/^https?:\/\//i.test(link)) {
      toast.error('Link must start with http:// or https://');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role.trim(),
        description: form.description.trim(),
        link,
        updatedAt: new Date(),
      };
      if (photoFile) {
        payload.photoUrl = await uploadToStorage(photoFile, 'staff');
      }
      if (editing) {
        await updateDoc(doc(db, 'staff', editing.id), payload);
        toast.success('Staff member updated.');
      } else {
        await addDoc(collection(db, 'staff'), {
          ...payload,
          order: items.length,
          createdAt: new Date(),
        });
        toast.success('Staff member added.');
      }
      setDialogOpen(false);
      await fetchStaff();
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'staff', deleteTarget.id));
      toast.success('Staff member removed.');
      setDeleteTarget(null);
      await fetchStaff();
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    }
  };

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Site content"
        title="Staff & contributors"
        description="The people listed on the public /staff page. Roles and descriptions should be accurate enough to cite professionally."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Add person
          </Button>
        }
      />

      {loading ? (
        <ul className="grid grid-cols-1 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <li
              key={i}
              className="h-24 animate-pulse rounded-md border border-border/60 bg-muted/40"
            />
          ))}
        </ul>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border/60 px-6 py-16 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Empty
          </p>
          <p className="mt-3 text-sm text-muted-foreground">Nobody listed yet. Add someone.</p>
          <div className="mt-5">
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add person
            </Button>
          </div>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3">
          {items.map((s) => (
            <li key={s.id}>
              <Card className="border-border/60 bg-background/40 transition-colors hover:border-foreground/40">
                <CardContent className="flex items-start gap-5 pt-6">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/60 bg-muted/20">
                    {s.photoUrl ? (
                      <img src={s.photoUrl} alt={s.name} className="h-full w-full object-cover" />
                    ) : (
                      <UserRound className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-display text-lg font-semibold">{s.name}</h3>
                    <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                      {s.role}
                    </div>
                    {s.description && (
                      <p className="mt-2 text-sm text-muted-foreground">{s.description}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(s)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit person' : 'Add person'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <Field label="Name" required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ferb"
                required
              />
            </Field>
            <Field label="Role" required hint="e.g. Developer, Tournament admin, Mapper.">
              <Input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="Developer"
                required
              />
            </Field>
            <Field label="Description" hint="Optional. One or two sentences about their work.">
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </Field>
            <Field label="Link" hint="Optional. Portfolio, GitHub, or Steam profile.">
              <Input
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="https://github.com/…"
              />
            </Field>
            <Field label="Photo" hint="Optional. Square photos crop best.">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove person?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently removes{' '}
            <span className="font-medium text-foreground">{deleteTarget?.name}</span> from the
            public staff page.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;
